import React, { useEffect, useState } from 'react';
import { Clock, Trash2, RefreshCw } from 'lucide-react';
import { useStore } from '../../stores';
import { db, DBHistoryEntry } from '../../db/schema';
import { onHistoryChange } from '../../lib/http';

// Default workspace ID for local history when no workspace is selected
const DEFAULT_LOCAL_WORKSPACE = 'local-workspace';

export function HistoryList() {
  const { openTab, activeWorkspaceId } = useStore();
  // Use active workspace or default to local workspace for history
  const effectiveWorkspaceId = activeWorkspaceId || DEFAULT_LOCAL_WORKSPACE;
  const [history, setHistory] = useState<DBHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load history from IndexedDB
  const loadHistory = async () => {
    try {
      setIsLoading(true);
      const entries = await db.getRecentHistory(effectiveWorkspaceId, 50);
      setHistory(entries);
    } catch (error) {
      console.error('Failed to load history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, [effectiveWorkspaceId]);

  // Subscribe to history changes
  useEffect(() => {
    const unsubscribe = onHistoryChange(() => {
      loadHistory();
    });
    return unsubscribe;
  }, [effectiveWorkspaceId]);

  const handleHistoryClick = (entry: DBHistoryEntry) => {
    // Open a new tab with the history entry's request data
    const tabId = `history-${entry.id}`;
    openTab({
      id: tabId,
      type: 'request',
      title: entry.request.name || entry.request.url || 'Untitled',
      request: {
        id: tabId,
        name: entry.request.name || 'From History',
        method: entry.request.method,
        url: entry.request.url,
        headers: entry.request.headers,
        params: entry.request.params,
        body: entry.request.body,
        auth: entry.request.auth,
      },
      isDirty: false,
      isLoading: false,
      // Pre-populate response if available
      response: entry.response ? {
        status: entry.response.status,
        statusText: entry.response.statusText,
        headers: entry.response.headers,
        body: entry.response.body,
        size: entry.response.size,
        time: entry.response.time,
      } : undefined,
    });
  };

  const handleClearHistory = async () => {
    try {
      await db.clearHistory(effectiveWorkspaceId);
      setHistory([]);
    } catch (error) {
      console.error('Failed to clear history:', error);
    }
  };

  const getMethodColor = (method: string) => {
    const colors: Record<string, string> = {
      GET: 'text-blue-400',
      POST: 'text-green-400',
      PUT: 'text-orange-400',
      PATCH: 'text-cyan-400',
      DELETE: 'text-red-400',
      HEAD: 'text-purple-400',
      OPTIONS: 'text-blue-600',
    };
    return colors[method.toUpperCase()] || 'text-gray-400';
  };

  const getStatusColor = (status?: number) => {
    if (!status) return 'text-gray-400';
    if (status >= 200 && status < 300) return 'text-green-400';
    if (status >= 300 && status < 400) return 'text-yellow-400';
    if (status >= 400 && status < 500) return 'text-orange-400';
    if (status >= 500) return 'text-red-400';
    return 'text-gray-400';
  };

  const formatTimestamp = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;

    // Less than 1 minute
    if (diff < 60 * 1000) {
      return 'Just now';
    }

    // Less than 1 hour
    if (diff < 60 * 60 * 1000) {
      const minutes = Math.floor(diff / (60 * 1000));
      return `${minutes}m ago`;
    }

    // Less than 24 hours
    if (diff < 24 * 60 * 60 * 1000) {
      const hours = Math.floor(diff / (60 * 60 * 1000));
      return `${hours}h ago`;
    }

    // More than 24 hours - show date
    const date = new Date(timestamp);
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const truncateUrl = (url: string, maxLength: number = 30) => {
    if (url.length <= maxLength) return url;
    return url.substring(0, maxLength) + '...';
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="h-10 flex items-center justify-between px-3 border-b border-border">
        <span className="text-sm font-medium text-foreground flex items-center gap-2">
          <Clock className="w-4 h-4 text-muted-foreground" />
          History
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={loadHistory}
            className="p-1 rounded hover:bg-accent transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4 text-muted-foreground" />
          </button>
          {history.length > 0 && (
            <button
              onClick={handleClearHistory}
              className="p-1 rounded hover:bg-accent transition-colors"
              title="Clear History"
            >
              <Trash2 className="w-4 h-4 text-muted-foreground" />
            </button>
          )}
        </div>
      </div>

      {/* History List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="text-center py-8">
            <p className="text-sm text-muted-foreground">Loading history...</p>
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-8">
            <Clock className="w-8 h-8 text-muted-foreground mx-auto mb-2 opacity-50" />
            <p className="text-sm text-muted-foreground">No history yet</p>
            <p className="text-xs text-muted-foreground mt-1">
              Send requests to see them here
            </p>
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {history.map((entry) => (
              <div
                key={entry.id}
                onClick={() => handleHistoryClick(entry)}
                className="flex flex-col gap-1 p-2 rounded-md hover:bg-accent cursor-pointer group transition-colors"
              >
                {/* Method + URL row */}
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-mono font-semibold flex-shrink-0 ${getMethodColor(entry.request.method)}`}>
                    {entry.request.method}
                  </span>
                  <span className="text-sm text-foreground truncate flex-1" title={entry.request.url}>
                    {truncateUrl(entry.request.url)}
                  </span>
                </div>

                {/* Status + Time row */}
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    {entry.response ? (
                      <>
                        <span className={`font-medium ${getStatusColor(entry.response.status)}`}>
                          {entry.response.status}
                        </span>
                        <span className="text-muted-foreground">
                          {entry.response.time}ms
                        </span>
                      </>
                    ) : entry.error ? (
                      <span className="text-red-400">Error</span>
                    ) : (
                      <span className="text-muted-foreground">No response</span>
                    )}
                  </div>
                  <span className="text-muted-foreground">
                    {formatTimestamp(entry.executedAt)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
